import React, { FC, useState, useEffect } from 'react'
import { Users } from 'lucide-react'
import { useSessionHistory } from '../hooks/useSessionHistory'
import { useQueueActions } from '../hooks/useQueueActions'
import { SellerConfigItem, SellerServiceSessionItem, SellerServiceSessionCustomerItem } from '../types'
import { addSessionCustomer, completeSessionCustomer, getSessionCustomers } from '../services'
import Swal from 'sweetalert2'
import ReturnToQueueModal, { ReturnToQueueData } from './ReturnToQueueModal'
import PasswordConfirmModal from './PasswordConfirmModal'
import {i18n} from '../../../../translate/i18n'
import { SessionCustomerResultModal } from './SessionCustomerResultModal'

interface InServiceTabProps {
  pluginspaceId: number
  currentUserId: number
  isPrivileged: boolean
  sellerConfig?: SellerConfigItem | null
  onQueueChanged?: () => void
}

export const InServiceTab: FC<InServiceTabProps> = ({
  pluginspaceId,
  currentUserId,
  isPrivileged,
  sellerConfig,
  onQueueChanged,
}) => {
  const { sessions, loading, loadHistory } = useSessionHistory(pluginspaceId)
  const { closeSellerAttendance, returnSellerToQueue, loading: actionLoading } =
    useQueueActions(pluginspaceId)
  const [inProgressSessions, setInProgressSessions] = useState<SellerServiceSessionItem[]>([])
  const [returnToQueueSession, setReturnToQueueSession] = useState<SellerServiceSessionItem | null>(null)
  const [finishAttendanceSession, setFinishAttendanceSession] = useState<SellerServiceSessionItem | null>(null)
  const [sessionCustomers, setSessionCustomers] = useState<Record<number, SellerServiceSessionCustomerItem[]>>({})
  const [selectedCustomer, setSelectedCustomer] = useState<SellerServiceSessionCustomerItem | null>(null)
  const [customerActionLoading, setCustomerActionLoading] = useState(false)
  const [multiReturnSession, setMultiReturnSession] = useState<SellerServiceSessionItem | null>(null)
  const [multiCloseSession, setMultiCloseSession] = useState<SellerServiceSessionItem | null>(null)

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  useEffect(() => {
    const inProgress = sessions.filter((s) => s.status === 'in_progress')
    setInProgressSessions(inProgress)
    void Promise.all(inProgress.map(async (session) => {
      try {
        const customers = await getSessionCustomers(session.id)
        setSessionCustomers((previous) => ({ ...previous, [session.id]: customers }))
      } catch {
        setSessionCustomers((previous) => ({ ...previous, [session.id]: [] }))
      }
    }))
  }, [sessions])

  const getCustomers = (sessionId: number) => sessionCustomers[sessionId] || []

  const isMultiCustomerSession = (sessionId: number) =>
    sellerConfig?.multiCustomerEnabled === true &&
    Number(sellerConfig.maxCustomersPerSession) > 1 &&
    getCustomers(sessionId).length > 0

  const handleAddCustomer = async (session: SellerServiceSessionItem) => {
    try {
      setCustomerActionLoading(true)
      const customer = await addSessionCustomer(session.id)
      setSessionCustomers((previous) => ({
        ...previous,
        [session.id]: [...getCustomers(session.id), customer],
      }))
    } catch (error) {
      Swal.fire('Erro', error instanceof Error ? error.message : 'Não foi possível adicionar o cliente', 'error')
    } finally {
      setCustomerActionLoading(false)
    }
  }

  const handleCustomerResult = async (data: { outcome: 'sale_completed' | 'no_sale'; saleAmount?: number; reasonCode?: number; note?: string }) => {
    if (!selectedCustomer) return
    try {
      setCustomerActionLoading(true)
      const updated = await completeSessionCustomer(selectedCustomer.id, data.outcome, data.saleAmount, data.reasonCode, data.note)
      const refreshedCustomers = await getSessionCustomers(updated.sessionId)
      setSessionCustomers((previous) => ({
        ...previous,
        [updated.sessionId]: refreshedCustomers,
      }))
      setSelectedCustomer(null)
    } catch (error) {
      Swal.fire('Erro', error instanceof Error ? error.message : 'Não foi possível salvar o resultado', 'error')
    } finally {
      setCustomerActionLoading(false)
    }
  }

  const handleFinishSession = (session: SellerServiceSessionItem) => {
    if (isMultiCustomerSession(session.id)) {
      setMultiCloseSession(session)
      return
    }
    setFinishAttendanceSession(session)
  }

  const getMultiSessionSummary = (session: SellerServiceSessionItem) => {
    const customers = getCustomers(session.id)
    const saleAmount = customers.reduce((total, customer) => {
      return customer.outcome === 'sale_completed' ? total + Number(customer.saleAmount || 0) : total
    }, 0)
    return {
      outcome: customers.some((customer) => customer.outcome === 'sale_completed') ? 'sale_completed' as const : 'no_sale' as const,
      saleAmount,
    }
  }

  const handleFinishSessionConfirm = async (data: ReturnToQueueData) => {
    if (!finishAttendanceSession) return

    const success = await closeSellerAttendance(
      finishAttendanceSession.id,
      finishAttendanceSession.sellerId,
      data.password,
      data.saleOutcome === 'completed' ? 'sale_completed' : 'no_sale',
      data.noSaleReasonId,
      undefined,
      data.saleAmount
    )

    setFinishAttendanceSession(null)

    if (success) {
      Swal.fire('Sucesso', 'Atendimento encerrado', 'success')
      loadHistory()
      onQueueChanged?.()
    } else {
      Swal.fire('Erro', 'Erro ao encerrar atendimento', 'error')
    }
  }

  const handleReturnToQueue = (session: SellerServiceSessionItem) => {
    if (isMultiCustomerSession(session.id)) {
      setMultiReturnSession(session)
      return
    }
    setReturnToQueueSession(session)
  }

  const handleMultiReturnConfirm = async (credential: string) => {
    if (!multiReturnSession) return
    const summary = getMultiSessionSummary(multiReturnSession)
    const success = await returnSellerToQueue(
      multiReturnSession.id,
      multiReturnSession.sellerId,
      credential,
      summary.outcome,
      undefined,
      undefined,
      summary.saleAmount,
    )
    setMultiReturnSession(null)
    if (success) {
      Swal.fire('Sucesso', 'Vendedor retornado para o final da fila', 'success')
      loadHistory()
      onQueueChanged?.()
    } else {
      Swal.fire('Erro', 'Não foi possível retornar o vendedor para a fila', 'error')
    }
  }

  const handleMultiCloseConfirm = async (credential: string) => {
    if (!multiCloseSession) return
    const summary = getMultiSessionSummary(multiCloseSession)
    const success = await closeSellerAttendance(
      multiCloseSession.id,
      multiCloseSession.sellerId,
      credential,
      summary.outcome,
      undefined,
      undefined,
      summary.saleAmount,
    )
    setMultiCloseSession(null)
    if (success) {
      Swal.fire('Sucesso', 'Atendimento encerrado', 'success')
      loadHistory()
      onQueueChanged?.()
    } else {
      Swal.fire('Erro', 'Não foi possível encerrar o atendimento', 'error')
    }
  }

  const handleReturnToQueueConfirm = async (data: ReturnToQueueData) => {
    if (!returnToQueueSession) return

    const success = await returnSellerToQueue(
      returnToQueueSession.id,
      returnToQueueSession.sellerId,
      data.password,
      data.saleOutcome === 'completed' ? 'sale_completed' : 'no_sale',
      data.noSaleReasonId,
      undefined,
      data.saleAmount
    )

    setReturnToQueueSession(null)

    if (success) {
      Swal.fire('Sucesso', 'Vendedor retornado para o final da fila', 'success')
      loadHistory()
      onQueueChanged?.()
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Sem permissão',
        text: 'Não foi possível retornar o vendedor. Verifique se a senha está correta e se você tem permissão para esta ação.',
      })
    }
  }

  const visibleSessions = isPrivileged
    ? inProgressSessions
    : inProgressSessions.filter((session) => session.sellerId === currentUserId)

  const translateStatus = (status: string) => {
    const normalized = status.toLowerCase()
    const key = `seller_queue.status.${normalized}`
    const translated = String(i18n.t(key))

    if (translated !== key) {
      return translated
    }

    switch (normalized) {
      case 'in_progress':
        return 'Em atendimento'
      case 'finished':
        return 'Finalizado'
      case 'waiting':
        return 'Aguardando'
      default:
        return status
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  const formatCustomerDuration = (customer: SellerServiceSessionCustomerItem) => {
    const startedAt = new Date(customer.startedAt).getTime()
    const finishedAt = customer.finishedAt
      ? new Date(customer.finishedAt).getTime()
      : Date.now()
    return formatDuration(Math.max(0, Math.round((finishedAt - startedAt) / 1000)))
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>Em Atendimento ({visibleSessions.length})</h3>
      </div>
      <div className='card-body'>
        {visibleSessions.length === 0 ? (
          <div className='text-center py-10'>
            <p className='text-muted'>Nenhum atendimento em progresso</p>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800'>
                  <th>Vendedor</th>
                  <th>Clientes</th>
                  <th>Início</th>
                  <th>Duração</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {visibleSessions.map((session) => (
                  <React.Fragment key={session.id}>
                  <tr>
                    <td>
                      <span className='fw-bold'>{session.sellerName || 'Vendedor ' + session.sellerId}</span>
                    </td>
                    <td>
                      <div className='d-flex flex-column gap-1'>
                        <small className='text-muted'>
                          {getCustomers(session.id).length} cliente(s)
                        </small>
                        <div className='d-flex flex-wrap gap-1'>
                          {getCustomers(session.id).map((customer) => (
                            <button
                              key={customer.id}
                              type='button'
                              className={`btn btn-sm ${customer.status === 'in_progress' ? 'btn-light-warning' : 'btn-light-success'}`}
                              onClick={() => customer.status === 'in_progress' && setSelectedCustomer(customer)}
                              title={`Cliente ${customer.sequenceNumber} - ${formatCustomerDuration(customer)}`}
                            >
                              Cliente {customer.sequenceNumber}: {customer.status === 'in_progress' ? 'pendente' : 'concluído'} ({formatCustomerDuration(customer)})
                            </button>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td>
                      <small>{new Date(session.startedAt).toLocaleTimeString('pt-BR')}</small>
                    </td>
                    <td>
                      {(() => {
                        const duration = Math.round(
                          (new Date().getTime() - new Date(session.startedAt).getTime()) / 1000
                        )
                        return formatDuration(duration)
                      })()}
                    </td>
                    <td>
                      <span className='badge badge-light-info'>{translateStatus(session.status)}</span>
                    </td>
                    <td>
                      <div className='d-flex flex-column gap-2'>
                        {sellerConfig?.multiCustomerEnabled === true &&
                          Number(sellerConfig.maxCustomersPerSession) > 1 &&
                          getCustomers(session.id).length < Number(sellerConfig.maxCustomersPerSession) && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => void handleAddCustomer(session)}
                              disabled={customerActionLoading}
                              title='Adicionar cliente anônimo'
                              aria-label='Adicionar cliente anônimo'
                            >
                              <Users size={16} />
                            </button>
                          )}
                        <div className='d-flex gap-2'>
                          <button
                            className='btn btn-sm btn-danger'
                            onClick={() => handleFinishSession(session)}
                            disabled={actionLoading || customerActionLoading || getCustomers(session.id).some((customer) => customer.status === 'in_progress')}
                            title='Encerra a sessão e remove o vendedor da fila'
                          >
                            <i className='fas fa-sign-out-alt'></i>
                          </button>
                          <button
                            className='btn btn-sm btn-primary'
                            onClick={() => handleReturnToQueue(session)}
                            disabled={actionLoading || customerActionLoading || getCustomers(session.id).some((customer) => customer.status === 'in_progress')}
                            title='Retornar à fila'
                          >
                            <i className='fas fa-undo-alt'></i>
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReturnToQueueModal
        isOpen={!!returnToQueueSession}
        sellerName={returnToQueueSession?.sellerName || `Vendedor ${returnToQueueSession?.sellerId}`}
        onConfirm={handleReturnToQueueConfirm}
        onCancel={() => setReturnToQueueSession(null)}
      />

      <PasswordConfirmModal
        isOpen={!!multiCloseSession}
        title='Encerrar sessão'
        description='Os resultados individuais já foram registrados. Informe a senha do vendedor, gerente ou administrador:'
        onConfirm={(credential) => void handleMultiCloseConfirm(credential)}
        onCancel={() => setMultiCloseSession(null)}
      />

      <PasswordConfirmModal
        isOpen={!!multiReturnSession}
        title='Retornar à fila'
        description='Os resultados individuais já foram registrados. Informe apenas a credencial para retornar o vendedor à fila:'
        onConfirm={(credential) => void handleMultiReturnConfirm(credential)}
        onCancel={() => setMultiReturnSession(null)}
      />

      <SessionCustomerResultModal
        customer={selectedCustomer}
        isLoading={customerActionLoading}
        onConfirm={(data) => void handleCustomerResult(data)}
        onCancel={() => setSelectedCustomer(null)}
      />

      <ReturnToQueueModal
        isOpen={!!finishAttendanceSession}
        title='Encerrar Atendimento'
        introText='Vendedor:'
        passwordIntroText='Para confirmar o encerramento do atendimento, informe a senha do vendedor, do gerente ou do administrador:'
        confirmText='Confirmar Encerramento'
        sellerName={finishAttendanceSession?.sellerName || `Vendedor ${finishAttendanceSession?.sellerId}`}
        onConfirm={handleFinishSessionConfirm}
        onCancel={() => setFinishAttendanceSession(null)}
      />
    </div>
  )
}
